import { useState, useCallback } from 'react';
import type { Workbook } from 'exceljs';

import {
  validateTableData,
  type ValidationResult,
  type ColumnMappings,
} from '../utils';

type ExcelCellValue = string | number | boolean | Date | null;
type ExcelRowData = ExcelCellValue[];
type ExcelSheetData = ExcelRowData[];

interface UseExcelProcessorOptions {
  maxPreviewRows?: number;
  maxPreviewColumns?: number;
}

interface ExcelProcessorState {
  file: File | null;
  worksheetNames: string[];
  selectedWorksheet: string;
  previewData: ExcelSheetData; // Limited rows for UI performance (when needed)
  fullData: ExcelSheetData; // Complete worksheet data for validation and display
  isProcessing: boolean;
  error: string | null;
  validation: ValidationResult | null;
}

export const useExcelProcessor = (options: UseExcelProcessorOptions = {}) => {
  const { maxPreviewRows = 20, maxPreviewColumns = 10 } = options;

  const [state, setState] = useState<ExcelProcessorState>({
    file: null,
    worksheetNames: [],
    selectedWorksheet: '',
    previewData: [],
    fullData: [],
    isProcessing: false,
    error: null,
    validation: null,
  });

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const processWorksheet = useCallback(
    async (workbook: Workbook, worksheetName: string, limitRows?: boolean) => {
      const worksheet = workbook.getWorksheet(worksheetName);
      if (!worksheet) return [];

      const jsonData: unknown[][] = [];
      // Use full data by default, or limit for display if specified
      const maxRows = limitRows
        ? Math.min(maxPreviewRows, worksheet.rowCount)
        : worksheet.rowCount;
      const maxCols = limitRows
        ? Math.min(maxPreviewColumns, worksheet.columnCount)
        : worksheet.columnCount;

      for (let row = 1; row <= maxRows; row++) {
        const rowData: unknown[] = [];
        for (let col = 1; col <= maxCols; col++) {
          const cell = worksheet.getCell(row, col);
          let cellValue: unknown = cell.value;

          if (cell.value instanceof Date) {
            cellValue = cell.value.toISOString();
          } else if (typeof cell.value === 'object' && cell.value !== null) {
            cellValue = String(cell.value);
          }

          rowData.push(cellValue);
        }
        jsonData.push(rowData);
      }

      return jsonData as ExcelSheetData;
    },
    [maxPreviewRows, maxPreviewColumns]
  );

  const loadFile = useCallback(
    async (file: File) => {
      setState((prev) => ({ ...prev, isProcessing: true, error: null, file }));

      try {
        const ExcelJS = await import('exceljs');
        const arrayBuffer = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const sheetNames = workbook.worksheets.map((ws) => ws.name);
        const firstSheet = sheetNames[0] || '';

        let previewData: ExcelSheetData = [];
        let fullData: ExcelSheetData = [];
        if (firstSheet) {
          // Load preview data (limited for display performance)
          previewData = await processWorksheet(workbook, firstSheet, true);
          // Load full data (for validation and processing)
          fullData = await processWorksheet(workbook, firstSheet, false);
        }

        setState((prev) => ({
          ...prev,
          worksheetNames: sheetNames,
          selectedWorksheet: firstSheet,
          previewData,
          fullData,
          isProcessing: false,
        }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          error:
            "Failed to read Excel file. Please ensure it's a valid Excel format.",
          isProcessing: false,
        }));
      }
    },
    [processWorksheet]
  );

  const changeWorksheet = useCallback(
    async (worksheetName: string) => {
      if (!state.file || !worksheetName) return;

      setState((prev) => ({ ...prev, selectedWorksheet: worksheetName }));

      try {
        const ExcelJS = await import('exceljs');
        const arrayBuffer = await state.file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const previewData = await processWorksheet(
          workbook,
          worksheetName,
          true
        );
        const fullData = await processWorksheet(workbook, worksheetName, false);
        setState((prev) => ({ ...prev, previewData, fullData }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          error: 'Failed to load worksheet data',
        }));
      }
    },
    [state.file, processWorksheet]
  );

  const reset = useCallback(() => {
    setState({
      file: null,
      worksheetNames: [],
      selectedWorksheet: '',
      previewData: [],
      fullData: [],
      isProcessing: false,
      error: null,
      validation: null,
    });
  }, []);

  const validateFile = useCallback((file: File): boolean => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const validExtensions = ['.xlsx', '.xls'];

    return (
      validTypes.includes(file.type) ||
      validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    );
  }, []);

  const validateData = useCallback(
    (columnMappings: ColumnMappings) => {
      if (state.fullData.length === 0) {
        setState((prev) => ({ ...prev, validation: null }));
        return;
      }

      const validation = validateTableData(
        state.fullData,
        columnMappings,
        true
      );
      setState((prev) => ({ ...prev, validation }));
    },
    [state.fullData]
  );

  return {
    ...state,
    loadFile,
    changeWorksheet,
    reset,
    setError,
    validateFile,
    validateData,
  };
};
